import { IGoodsDetail, IProductDetail } from '@/api/types/public';
import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { isFaceWidthMeasurementEntryAvailable } from '@/domain/lens/measurement';
import {
  SizeChartMeasurementPrompt,
  useActiveFaceMeasurement,
} from '@/domain/lens/measurement/client';
import { useStore } from '@/providers/app-store-provider';
import { IS_UK } from '@/utils/constants';
import { formatNumber } from '@/utils/tools';
import { Box } from '@mui/material';
import { useLocale } from 'next-intl';
import { useTranslations } from '@/i18n/use-translations';
import { useContext, useMemo, useState } from 'react';
import { useFrameSize } from './hooks/useFrameSize';
import { useFrameSizeAfterFormat } from './hooks/useFrameSizeAfterFormat';

interface SizeGuideProps {
  show?: boolean;
  onClose?: () => void;
  detail?: IProductDetail;
  isWeb?: boolean;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

const SizeGuide: React.FC<SizeGuideProps> = ({
  detail,
  isWeb = false,
  SDProductDetailsPageBtnClick,
}) => {
  const inches = useStore((state) => state.inches);
  const setInches = useStore((state) => state.setInches);
  const { totalLengthValue, glassWidthValue, lensHeightValue, legLengthValue, bridgeValue } =
    useFrameSizeAfterFormat(detail as IGoodsDetail['product'], inches);
  const t = useTranslations('goodsDetail.sizeGuide');
  const measurementT = useTranslations('lensMeasurement');
  const faceMeasurement = useActiveFaceMeasurement();
  const locale = useLocale();
  const { deviceType } = useContext(ResponsiveContext);
  const showFaceWidthMeasurementEntry = isFaceWidthMeasurementEntryAvailable({
    isUk: IS_UK,
    deviceType,
  });
  // Web端和Wap端规格列表部分始终使用原始值（毫米），不响应单位切换
  const fixedFrameSize = useFrameSize(detail as IGoodsDetail['product'], false);

  // 根据 locale 确定数字格式语言
  const lang = useMemo(() => {
    if (locale === 'de') {
      return 'de-DE';
    }
    if (locale === 'fr') {
      return 'fr-FR';
    }
    return 'en-US';
  }, [locale]);

  const [showSize1, setShowSize1] = useState(false);
  const [showSize2, setShowSize2] = useState(false);

  const handleImageLoad = (flag: 'showSize1' | 'showSize2') => {
    if (flag === 'showSize1') {
      setShowSize1(true);
    } else {
      setShowSize2(true);
    }
  };

  return (
    <Box className="relative">
      <Box>
        <Box className="border border-[#d4d4d4] rounded-[8px] overflow-hidden">
          <table className={`text-[12px] w-full ${isWeb ? 'text-[14px]' : ''}`}>
            <tbody>
              {detail?.crowd !== '儿童' ? (
                <>
                  <tr>
                    <th
                      className={`text-left bg-[#f7f7f7] ${isWeb ? 'pl-[16px] leading-[40px] w-[146px]' : 'pl-[12px] leading-[38px] w-[104px]'}`}
                    >
                      {t('table.size')}
                    </th>
                    <th
                      className={`border-l border-[#d4d4d4] font-bb text-left ${isWeb ? 'pl-[16px] leading-[40px]' : 'pl-[12px] leading-[38px]'}`}
                    >
                      {t('table.frameWidth')}
                    </th>
                  </tr>
                  <tr>
                    <td
                      className={`border-t border-[#d4d4d4] bg-[#f7f7f7] ${isWeb ? 'pl-[16px] leading-[40px] w-[146px]' : 'pl-[12px] leading-[38px] w-[104px]'}`}
                    >
                      {t('table.narrow')}
                    </td>
                    <td
                      className={`border-t border-l border-[#d4d4d4] font-bb ${isWeb ? 'pl-[16px] leading-[40px]' : 'pl-[12px] leading-[38px]'}`}
                    >
                      {t('measurements.adult.narrow')}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className={`border-t border-[#d4d4d4] bg-[#f7f7f7] ${isWeb ? 'pl-[16px] leading-[40px] w-[146px]' : 'pl-[12px] leading-[38px] w-[104px]'}`}
                    >
                      {t('table.medium')}
                    </td>
                    <td
                      className={`border-t border-l border-[#d4d4d4] font-bb ${isWeb ? 'pl-[16px] leading-[40px]' : 'pl-[12px] leading-[38px]'}`}
                    >
                      {t('measurements.adult.medium')}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className={`border-t border-[#d4d4d4] bg-[#f7f7f7] ${isWeb ? 'pl-[16px] leading-[40px] w-[146px]' : 'pl-[12px] leading-[38px] w-[104px]'}`}
                    >
                      {t('table.wide')}
                    </td>
                    <td
                      className={`border-t border-l border-[#d4d4d4] font-bb ${isWeb ? 'pl-[16px] leading-[40px]' : 'pl-[12px] leading-[38px]'}`}
                    >
                      {t('measurements.adult.wide')}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className={`border-t border-[#d4d4d4] bg-[#f7f7f7] ${isWeb ? 'pl-[16px] leading-[40px] w-[146px]' : 'pl-[12px] leading-[38px] w-[104px]'}`}
                    >
                      {t('table.extraWide')}
                    </td>
                    <td
                      className={`border-t border-l border-[#d4d4d4] font-bb ${isWeb ? 'pl-[16px] leading-[40px]' : 'pl-[12px] leading-[38px]'}`}
                    >
                      {t('measurements.adult.extraWide')}
                    </td>
                  </tr>
                </>
              ) : (
                <>
                  <tr>
                    <th
                      className={`text-left bg-[#f7f7f7] ${isWeb ? 'pl-[16px] leading-[40px] w-[146px]' : 'pl-[12px] leading-[38px] w-[104px]'}`}
                    >
                      {t('table.size')}
                    </th>
                    <th
                      className={`border-l border-[#d4d4d4] font-bb text-left ${isWeb ? 'pl-[16px] leading-[40px]' : 'pl-[12px] leading-[38px]'}`}
                    >
                      {t('table.frameWidth')}
                    </th>
                  </tr>
                  <tr>
                    <td
                      className={`border-t border-[#d4d4d4] bg-[#f7f7f7] ${isWeb ? 'pl-[16px] leading-[40px] w-[146px]' : 'pl-[12px] leading-[38px] w-[104px]'}`}
                    >
                      {t('table.narrow')}
                    </td>
                    <td
                      className={`border-t border-l border-[#d4d4d4] font-bb ${isWeb ? 'pl-[16px] leading-[40px]' : 'pl-[12px] leading-[38px]'}`}
                    >
                      {t('measurements.children.narrow')}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className={`border-t border-[#d4d4d4] bg-[#f7f7f7] ${isWeb ? 'pl-[16px] leading-[40px] w-[146px]' : 'pl-[12px] leading-[38px] w-[104px]'}`}
                    >
                      {t('table.medium')}
                    </td>
                    <td
                      className={`border-t border-l border-[#d4d4d4] font-bb ${isWeb ? 'pl-[16px] leading-[40px]' : 'pl-[12px] leading-[38px]'}`}
                    >
                      {t('measurements.children.medium')}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className={`border-t border-[#d4d4d4] bg-[#f7f7f7] ${isWeb ? 'pl-[16px] leading-[40px] w-[146px]' : 'pl-[12px] leading-[38px] w-[104px]'}`}
                    >
                      {t('table.wide')}
                    </td>
                    <td
                      className={`border-t border-l border-[#d4d4d4] font-bb ${isWeb ? 'pl-[16px] leading-[40px]' : 'pl-[12px] leading-[38px]'}`}
                    >
                      {t('measurements.children.wide')}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </Box>

        {showFaceWidthMeasurementEntry && !faceMeasurement ? (
          <SizeChartMeasurementPrompt
            description={measurementT('sizeChart.description')}
            action={measurementT('sizeChart.action')}
          />
        ) : null}

        {/* 产品尺寸部分 */}
        <Box className={isWeb ? 'mt-[40px]' : 'mt-[28px]'}>
          {/* 标题和单位切换 */}
          <Box className={`flex justify-between items-center ${isWeb ? 'mb-[30px]' : 'mb-[30px]'}`}>
            <p className={`text-[16px] font-bb`}>{t('productDimensions')}</p>
            <Box
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setInches(!inches);
                SDProductDetailsPageBtnClick?.('show in inches chart');
              }}
              sx={{
                textDecoration: 'underline',
                color: '#808080',
                fontSize: isWeb ? '14px' : '12px',
                cursor: 'pointer',
              }}
            >
              {t('showIn', { unit: inches ? t('mm') : t('inches') })}
            </Box>
          </Box>

          {/* 图片容器 - web端左右布局，wap端上下布局 */}
          <Box className={isWeb ? 'flex space-between' : ''}>
            {/* 第一张图片 - 镜框尺寸 */}
            <Box className={`relative ${isWeb ? 'flex-1' : 'pb-[10px] mb-[35px]'}`}>
              <Box className={isWeb ? 'w-[300px] mx-auto' : 'w-[313px] mx-auto'}>
                <Image
                  src="/goods-detail/frame_2025.png"
                  alt="frame width and lens width"
                  width={isWeb ? 300 : 313}
                  height={isWeb ? 114 : 200}
                  className="block w-[100%]"
                  onLoad={() => handleImageLoad('showSize1')}
                />

                {showSize1 ? (
                  <>
                    {/* 镜框宽度 */}
                    {totalLengthValue ? (
                      <Box
                        className={`absolute -top-[8px] left-1/2 transform -translate-x-1/2 text-center ${isWeb ? 'text-[12px] leading-[12px]' : 'text-[10px] leading-[10px]'}`}
                      >
                        <span>{totalLengthValue}</span>
                      </Box>
                    ) : null}

                    {/* 镜片宽度 */}
                    {glassWidthValue ? (
                      <Box
                        className={`absolute transform -translate-x-1/2 text-center ${isWeb ? 'left-[26.5%] top-[59px] text-[12px] leading-[12px]' : 'top-[63px] left-[27.5%] text-[10px] leading-[10px]'}`}
                      >
                        <span>{glassWidthValue}</span>
                      </Box>
                    ) : null}

                    {/* 镜片高度 */}
                    {lensHeightValue ? (
                      <Box
                        className={`absolute transform translate-x-1/2 text-center ${isWeb ? 'text-[12px] leading-[12px] top-[58px] right-[26.3%]' : 'text-[10px] leading-[10px] top-[63px] right-[27.5%]'}`}
                      >
                        <span>{lensHeightValue}</span>
                      </Box>
                    ) : null}

                    {/* bridge宽度 */}
                    {bridgeValue ? (
                      <Box
                        className={`absolute left-1/2 transform -translate-x-1/2 text-center ${isWeb ? 'text-[12px] leading-[12px] bottom-[10px]' : 'text-[10px] leading-[10px] bottom-[16px]'}`}
                      >
                        <span>{bridgeValue}</span>
                      </Box>
                    ) : null}
                  </>
                ) : null}
              </Box>
            </Box>

            {/* 第二张图片 - 镜腿长度 */}
            <Box className={`relative ${isWeb ? 'flex-1' : ''}`}>
              <Box className={isWeb ? 'w-[300px] mx-auto relative' : 'w-[313px] mx-auto relative'}>
                <Image
                  src="/goods-detail/temple_2025.png"
                  alt="temple length"
                  width={isWeb ? 300 : 313}
                  height={isWeb ? 114 : 200}
                  className="block w-[100%]"
                  onLoad={() => handleImageLoad('showSize2')}
                />

                {showSize2 && legLengthValue ? (
                  <Box
                    className={`absolute bottom-[8px] right-[138px] text-center ${isWeb ? 'text-[12px] leading-[12px]' : 'text-[10px] leading-[10px]'}`}
                  >
                    <span>{legLengthValue}</span>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* 尺寸信息部分 */}
        <Box className={isWeb ? 'mt-[52px]' : 'mt-[40px]'}>
          {isWeb ? (
            // Web端：左右布局
            <Box className="flex-1">
              <h3 className="text-[16px] font-bb mb-[12px]">{t('gotAPair.title')}</h3>
              <p className="text-[14px] mb-[24px] leading-[22px]">{t('gotAPair.description')}</p>
              {/* 规格列表 */}
              <Box className="flex items-center justify-between">
                <Box>
                  {/* Lens Width */}
                  <Box className="flex items-center gap-x-[16px] mb-[20px]">
                    <Image
                      src="/goods-detail/size_guide/Lens_width_new.png"
                      alt="lens width"
                      width={48}
                      height={24}
                      className="w-[48px] h-[24px]"
                    />
                    <Box className="flex-1 flex">
                      <Box className="flex items-center bg-[#f4f4f4] px-[10px] rounded-[4px] h-[28px]">
                        <p className="text-[12px] text-[#808080]">
                          <span className="text-[#463ae8] font-bs">
                            {formatNumber(fixedFrameSize.glass_width, 2, lang, false)}
                          </span>
                          <span className="inline-block w-[12px] h-[12px] border border-[#808080] mx-[4px] translate-y-[2px]" />
                          <span>{formatNumber(fixedFrameSize.frame_bridge, 2, lang, false)}</span>
                          <span>-</span>
                          <span>
                            {formatNumber(fixedFrameSize.frame_leg_length, 2, lang, false)}
                          </span>
                        </p>
                      </Box>
                      <p className="text-[14px] font-bb ml-[12px] leading-[20px] flex-1 flex items-center">
                        {t('gotAPair.lensWidth')}
                      </p>
                    </Box>
                  </Box>
                  {/* Bridge Width */}
                  <Box className="flex items-center gap-x-[16px] mb-[20px]">
                    <Image
                      src="/goods-detail/size_guide/bridge_width_new.png"
                      alt="bridge width"
                      width={48}
                      height={24}
                      className="w-[48px] h-[24px]"
                    />
                    <Box className="flex-1 flex">
                      <Box className="flex items-center bg-[#f4f4f4] px-[10px] rounded-[4px] h-[28px]">
                        <p className="text-[12px] text-[#808080]">
                          <span>{formatNumber(fixedFrameSize.glass_width, 2, lang, false)}</span>
                          <span className="inline-block w-[12px] h-[12px] border border-[#808080] mx-[4px] translate-y-[2px]" />
                          <span className="text-[#463ae8] font-bs">
                            {formatNumber(fixedFrameSize.frame_bridge, 2, lang, false)}
                          </span>
                          <span>-</span>
                          <span>
                            {formatNumber(fixedFrameSize.frame_leg_length, 2, lang, false)}
                          </span>
                        </p>
                      </Box>
                      <p className="text-[14px] font-bb ml-[12px] leading-[20px] flex-1 flex items-center">
                        {t('gotAPair.bridgeWidth')}
                      </p>
                    </Box>
                  </Box>
                  {/* Temple Length */}
                  <Box className="flex items-center gap-x-[16px]">
                    <Image
                      src="/goods-detail/size_guide/temple_length_new.png"
                      alt="temple length"
                      width={48}
                      height={24}
                      className="w-[48px] h-[24px]"
                    />
                    <Box className="flex-1 flex items-center">
                      <Box className="flex items-center bg-[#f4f4f4] px-[10px] rounded-[4px] h-[28px]">
                        <p className="text-[12px] text-[#808080]">
                          <span>{formatNumber(fixedFrameSize.glass_width, 2, lang, false)}</span>
                          <span className="inline-block w-[12px] h-[12px] border border-[#808080] mx-[4px] translate-y-[2px]" />
                          <span>{formatNumber(fixedFrameSize.frame_bridge, 2, lang, false)}</span>
                          <span>-</span>
                          <span className="text-[#463ae8] font-bs">
                            {formatNumber(fixedFrameSize.frame_leg_length, 2, lang, false)}
                          </span>
                        </p>
                      </Box>
                      <p className="text-[14px] font-bb ml-[12px] leading-[20px] flex-1 flex items-center">
                        {t('gotAPair.templeLength')}
                      </p>
                    </Box>
                  </Box>
                </Box>
                {/* 右侧：眼镜图片 */}
                <Box className="flex-shrink-0">
                  <Image
                    src="/goods-detail/size_guide/side_view.png"
                    alt="eyeglasses example"
                    width={300}
                    height={200}
                    className="w-[300px]"
                  />
                </Box>
              </Box>
            </Box>
          ) : (
            // Wap端：上下布局
            <Box>
              <h3 className="text-[16px] font-bb mb-[12px]">{t('gotAPair.title')}</h3>
              <p className="text-[13px] mb-[12px] leading-[20px]">{t('gotAPair.description')}</p>
              {/* 规格列表 */}
              {/* 眼镜图片 */}
              <Box className="w-full mb-[16px]">
                <Image
                  src="/goods-detail/size_guide/side_view.png"
                  alt=""
                  width={350}
                  height={200}
                  className="w-full"
                />
              </Box>
              <Box className="mb-[20px] bg-[#f4f4f4] rounded-[6px] p-[16px]">
                {/* Lens Width */}
                <Box className="flex items-center gap-x-[12px] mb-[16px]">
                  <Image
                    src="/goods-detail/size_guide/Lens_width_new.png"
                    alt=""
                    width={40}
                    height={20}
                    className="w-[40px] h-[20px]"
                  />
                  <Box className="flex-1 flex">
                    <Box className="flex items-center bg-[#ffffff] px-[8px] rounded-[4px] h-[20px]">
                      <p className="text-[10px] text-[#808080]">
                        <span className="text-[#463ae8] font-bs">
                          {formatNumber(fixedFrameSize.glass_width, 2, lang, false)}
                        </span>
                        <span className="inline-block w-[7px] h-[7px] border border-[#808080] mx-[2px]" />
                        <span>{formatNumber(fixedFrameSize.frame_bridge, 2, lang, false)}</span>
                        <span>-</span>
                        <span>{formatNumber(fixedFrameSize.frame_leg_length, 2, lang, false)}</span>
                      </p>
                    </Box>
                    <p className="text-[12px] font-bb ml-[12px]">{t('gotAPair.lensWidth')}</p>
                  </Box>
                </Box>
                {/* Bridge Width */}
                <Box className="flex items-center gap-x-[12px] mb-[16px]">
                  <Image
                    src="/goods-detail/size_guide/bridge_width_new.png"
                    alt=""
                    width={40}
                    height={20}
                    className="w-[40px] h-[20px]"
                  />
                  <Box className="flex-1 flex">
                    <Box className="flex items-center bg-[#ffffff] px-[8px] rounded-[4px] h-[20px]">
                      <p className="text-[10px] text-[#808080]">
                        <span>{formatNumber(fixedFrameSize.glass_width, 2, lang, false)}</span>
                        <span className="inline-block w-[7px] h-[7px] border border-[#808080] mx-[2px]" />
                        <span className="text-[#463ae8] font-bs">
                          {formatNumber(fixedFrameSize.frame_bridge, 2, lang, false)}
                        </span>
                        <span>-</span>
                        <span>{formatNumber(fixedFrameSize.frame_leg_length, 2, lang, false)}</span>
                      </p>
                    </Box>
                    <p className="text-[12px] font-bb ml-[12px]">{t('gotAPair.bridgeWidth')}</p>
                  </Box>
                </Box>
                {/* Temple Length */}
                <Box className="flex items-center gap-x-[12px]">
                  <Image
                    src="/goods-detail/size_guide/temple_length_new.png"
                    alt=""
                    width={40}
                    height={20}
                    className="w-[40px] h-[20px]"
                  />
                  <Box className="flex-1 flex">
                    <Box className="flex items-center bg-[#ffffff] px-[8px] rounded-[4px] h-[20px]">
                      <p className="text-[10px] text-[#808080]">
                        <span>{formatNumber(fixedFrameSize.glass_width, 2, lang, false)}</span>
                        <span className="inline-block w-[7px] h-[7px] border border-[#808080] mx-[2px]" />
                        <span>{formatNumber(fixedFrameSize.frame_bridge, 2, lang, false)}</span>
                        <span>-</span>
                        <span className="text-[#463ae8] font-bs">
                          {formatNumber(fixedFrameSize.frame_leg_length, 2, lang, false)}
                        </span>
                      </p>
                    </Box>
                    <p className="text-[12px] font-bb ml-[12px]">{t('gotAPair.templeLength')}</p>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SizeGuide;
